import type { Result } from './result';

export const BOARD_SIZES = [4, 5] as const;
export type BoardSize = (typeof BOARD_SIZES)[number];

/** Host-selectable round lengths; the server accepts any whole second in the min–max range. */
export const ROUND_DURATIONS = [30, 60, 90, 120, 150] as const;
const MIN_ROUND_SECONDS = 30;
const MAX_ROUND_SECONDS = 600;
export const MIN_WORD_LENGTH = 3;
export const DICTIONARY_MAX_WORD_LENGTH = 17;
/**
 * Server-owned Versus room capacity; hosts cannot change it. Solo modes lock `maxPlayers`
 * to 1 in `modes.ts`. The value is part of `configKey`, so raising it starts new Versus
 * scoreboard buckets.
 */
export const MAX_PLAYERS = 24;
/**
 * Board difficulty selects by rank among generated candidates: 0 the easiest, 100 the
 * hardest playable one (docs/decisions/BOARD-DIFFICULTY.md). Absent in configs stored
 * before the field existed, so parsing treats absence as the default.
 */
export const MIN_DIFFICULTY = 0;
export const MAX_DIFFICULTY = 100;
export const DEFAULT_DIFFICULTY = 10;
export const COUNTDOWN_MS = 3_000;
/** Live Versus: how long guests have to confirm a host's start before the check is abandoned. */
export const READY_CHECK_MS = 10_000;
export const SUBMISSION_GRACE_MS = 500;
export const MAX_MESSAGE_BYTES = 8 * 1024;
/** Lobby chat: inputs bound new entries, history is truncated to the most recent few dozen. */
export const CHAT_MAX_LENGTH = 200;
export const CHAT_HISTORY_LIMIT = 50;
export const MAX_NAME_LENGTH = 14;
export const MAX_PROFILE_BLOCK_MESSAGE_LENGTH = 256;
/** Profile avatars; identity data chosen at onboarding and edited in Settings, never per room. */
export const AVATARS = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;
export type Avatar = (typeof AVATARS)[number];
export const DEFAULT_AVATAR: Avatar = '1';
/** Shown for a profile whose name onboarding never wrote (matches the D1 column default). */
export const DEFAULT_PLAYER_NAME = 'Player';

export function isAvatar(value: unknown): value is Avatar {
  return typeof value === 'string' && (AVATARS as readonly string[]).includes(value);
}
/** Accepted words per participant per round; the server enforces it, the client bounds retries below it. */
export const MAX_SUBMISSIONS_PER_PLAYER = 500;

/** Room codes omit the ambiguous glyphs 0/O and 1/I. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const ROOM_CODE_LENGTH = 6;
export const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);

export function isRoomCode(value: unknown): value is string {
  return typeof value === 'string' && ROOM_CODE_RE.test(value);
}

/** Typed or pasted text as a room code: upper case, alphabet characters only, at most the code length. */
export function normalizeRoomCode(value: string): string {
  return value
    .toUpperCase()
    .replace(new RegExp(`[^${ROOM_CODE_ALPHABET}]`, 'g'), '')
    .slice(0, ROOM_CODE_LENGTH);
}

/** Longest round ID accepted in messages, result URLs and API paths. */
export const MAX_ROUND_ID_LENGTH = 80;

export type ScoringMode = 'classic' | 'shared';

/**
 * `live`: one shared countdown and deadline for every participant. `async`: each
 * participant starts their own timed turn on the same board and the round completes
 * when everyone has finished. Absent in configs stored before the field existed, so
 * every check reads `pace === 'async'` and treats absence as live.
 */
export type RoundPace = 'live' | 'async';

/**
 * Versus rule set. `classic` is plain word hunting; `chaos` spawns power-ups on the shared
 * board (`game/power-ups.ts`) on top of `classic` scoring, and requires live pace, since every
 * participant sees the same spawns at the same moment. Absent in configs stored before the field existed, so every
 * check reads `variant === 'chaos'` and treats absence as classic.
 */
export const GAME_VARIANTS = ['classic', 'chaos'] as const;
export type GameVariant = (typeof GAME_VARIANTS)[number];

export interface GameConfig {
  boardSize: BoardSize;
  roundDuration: number;
  minWordLength: number;
  maxWordLength: number;
  /** Set by the server per mode (`MODE_CONFIG`), never by a host patch. */
  maxPlayers: number;
  scoringMode: ScoringMode;
  pace: RoundPace;
  difficulty: number;
  variant: GameVariant;
  /**
   * Live Versus: open the countdown on the host's start without asking guests to confirm.
   * Absent in configs stored before the field existed and treated as false (ready check on).
   */
  startAutomatically: boolean;
}

export const DEFAULT_CONFIG: GameConfig = {
  boardSize: 4,
  roundDuration: 90,
  minWordLength: 3,
  maxWordLength: 17,
  maxPlayers: MAX_PLAYERS,
  scoringMode: 'classic',
  pace: 'live',
  difficulty: DEFAULT_DIFFICULTY,
  variant: 'classic',
  startAutomatically: false,
};

/** Keys every stored or transmitted config must carry; `pace` (live), `difficulty` (default), `variant` (classic) and `startAutomatically` (false) may be absent. */
const REQUIRED_CONFIG_KEYS = (Object.keys(DEFAULT_CONFIG) as (keyof GameConfig)[]).filter(
  (key) => key !== 'pace' && key !== 'difficulty' && key !== 'variant' && key !== 'startAutomatically',
);

export function isBoardSize(value: unknown): value is BoardSize {
  return typeof value === 'number' && BOARD_SIZES.includes(value as BoardSize);
}

export function isScoringMode(value: unknown): value is ScoringMode {
  return value === 'classic' || value === 'shared';
}

function isRoundPace(value: unknown): value is RoundPace {
  return value === 'live' || value === 'async';
}

export function isAsyncPace(config: Pick<GameConfig, 'pace'> | null | undefined): boolean {
  return config?.pace === 'async';
}

export function isGameVariant(value: unknown): value is GameVariant {
  return typeof value === 'string' && (GAME_VARIANTS as readonly string[]).includes(value);
}

export function isChaosVariant(config: Pick<GameConfig, 'variant'> | null | undefined): boolean {
  return config?.variant === 'chaos';
}

export function isDifficulty(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isInteger(value) && value >= MIN_DIFFICULTY && value <= MAX_DIFFICULTY
  );
}

/**
 * Scoreboard partition; pace and difficulty are deliberately excluded so existing buckets
 * stay intact. Scores at different difficulties share a bucket until partitioning is designed.
 * Chaos power-ups change scores materially, so the variant is appended only when set: classic
 * keys are byte-identical to those archived before the variant existed.
 */
export function configKey(config: GameConfig): string {
  return [
    config.boardSize,
    config.roundDuration,
    config.minWordLength,
    config.maxWordLength,
    config.maxPlayers,
    config.scoringMode,
    ...(isChaosVariant(config) ? [config.variant] : []),
  ].join(':');
}

/** Validation failures carry the user-facing reason. */
export type ConfigResult = Result<GameConfig, string>;

export function validateConfig(input: unknown, fallback: GameConfig = DEFAULT_CONFIG): ConfigResult {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, error: 'Configuration must be an object' };
  }
  const raw = input as Record<string, unknown>;
  const boardSizeValue = raw.boardSize;
  const boardSize = boardSizeValue === undefined ? fallback.boardSize : boardSizeValue;
  const roundDuration =
    typeof raw.roundDuration === 'number'
      ? raw.roundDuration
      : raw.roundDuration === undefined
        ? fallback.roundDuration
        : Number.NaN;
  const minWordLength =
    typeof raw.minWordLength === 'number'
      ? raw.minWordLength
      : raw.minWordLength === undefined
        ? fallback.minWordLength
        : Number.NaN;
  const maxWordLength =
    typeof raw.maxWordLength === 'number'
      ? raw.maxWordLength
      : raw.maxWordLength === undefined
        ? fallback.maxWordLength
        : Number.NaN;
  const maxPlayers =
    typeof raw.maxPlayers === 'number'
      ? raw.maxPlayers
      : raw.maxPlayers === undefined
        ? fallback.maxPlayers
        : Number.NaN;
  const scoringMode = raw.scoringMode === undefined ? fallback.scoringMode : raw.scoringMode;
  const pace = raw.pace === undefined ? (fallback.pace ?? DEFAULT_CONFIG.pace) : raw.pace;
  const difficulty =
    raw.difficulty === undefined ? (fallback.difficulty ?? DEFAULT_CONFIG.difficulty) : raw.difficulty;
  const variant = raw.variant === undefined ? (fallback.variant ?? DEFAULT_CONFIG.variant) : raw.variant;
  const startAutomatically =
    raw.startAutomatically === undefined
      ? (fallback.startAutomatically ?? DEFAULT_CONFIG.startAutomatically)
      : raw.startAutomatically;

  if (!isBoardSize(boardSize)) return { ok: false, error: 'Board size must be 4 or 5' };
  if (
    !Number.isInteger(roundDuration) ||
    roundDuration < MIN_ROUND_SECONDS ||
    roundDuration > MAX_ROUND_SECONDS
  ) {
    return { ok: false, error: `Round length must be ${MIN_ROUND_SECONDS}–${MAX_ROUND_SECONDS} seconds` };
  }
  if (!Number.isInteger(minWordLength) || minWordLength < MIN_WORD_LENGTH) {
    return { ok: false, error: `Minimum word length must be at least ${MIN_WORD_LENGTH}` };
  }
  // Every board admits the dictionary's 17-letter ceiling (a 4×4 board offers 16 tiles plus Qu).
  if (
    !Number.isInteger(maxWordLength) ||
    maxWordLength < minWordLength ||
    maxWordLength > DICTIONARY_MAX_WORD_LENGTH
  ) {
    return {
      ok: false,
      error: `Maximum word length must be ${minWordLength}–${DICTIONARY_MAX_WORD_LENGTH}`,
    };
  }
  if (!Number.isInteger(maxPlayers) || maxPlayers < 1 || maxPlayers > MAX_PLAYERS) {
    return { ok: false, error: `Room capacity must be 1–${MAX_PLAYERS}` };
  }
  if (!isScoringMode(scoringMode)) return { ok: false, error: 'Scoring mode must be classic or shared' };
  if (!isRoundPace(pace)) return { ok: false, error: 'Pace must be live or async' };
  if (!isDifficulty(difficulty)) {
    return { ok: false, error: `Difficulty must be ${MIN_DIFFICULTY}–${MAX_DIFFICULTY}` };
  }
  if (!isGameVariant(variant)) return { ok: false, error: 'Mode must be classic or chaos' };
  if (typeof startAutomatically !== 'boolean') {
    return { ok: false, error: 'Start automatically must be on or off' };
  }
  if (variant === 'chaos' && pace === 'async') {
    return { ok: false, error: 'Chaos needs Live pace so everyone sees the same power-ups' };
  }
  if (variant === 'chaos' && scoringMode !== 'classic') {
    return { ok: false, error: 'Chaos plays with Classic scoring' };
  }

  return {
    ok: true,
    value: {
      boardSize,
      roundDuration,
      minWordLength,
      maxWordLength,
      maxPlayers,
      scoringMode,
      pace,
      difficulty,
      variant,
      startAutomatically,
    },
  };
}

export function mergeConfig(base: GameConfig, patch: unknown): ConfigResult {
  if (typeof patch !== 'object' || patch === null || Array.isArray(patch)) {
    return { ok: false, error: 'Configuration must be an object' };
  }
  return validateConfig({ ...base, ...(patch as Record<string, unknown>) }, base);
}

/**
 * A complete config with the optional fields that older stored configs lack filled with their
 * defaults, or null when invalid. Callers must use the returned value, not the input.
 */
export function parseGameConfig(value: unknown): GameConfig | null {
  if (!value || typeof value !== 'object') return null;
  if (!REQUIRED_CONFIG_KEYS.every((key) => Object.hasOwn(value, key))) return null;
  const result = validateConfig(value);
  return result.ok ? result.value : null;
}

export function isGameConfig(value: unknown): value is GameConfig {
  return parseGameConfig(value) !== null;
}
